// /utils/inViewObserverManager.js
let observer = null
let tracked = new Set()

const isInViewportNow = (
	el,
	rootMargin = '0px 0px -10% 0px',
	threshold = 0.15
) => {
	const rect = el.getBoundingClientRect()
	const vh = window.innerHeight || document.documentElement.clientHeight
	const vw = window.innerWidth || document.documentElement.clientWidth

	// parse bottom rootMargin shrink
	const parts = rootMargin.split(/\s+/)
	const b = parts[2] || '0px'
	const bottomShrink = b.endsWith('%')
		? (parseFloat(b) / 100) * vh
		: parseFloat(b)

	const effectiveBottom = vh - (isNaN(bottomShrink) ? 0 : bottomShrink)

	const topVisible = rect.top < effectiveBottom
	const bottomVisible = rect.bottom > threshold * vh
	const horizontallyVisible = rect.left < vw && rect.right > 0

	return topVisible && bottomVisible && horizontallyVisible
}

export function createInViewObserver({
	root = null,
	rootMargin = '0px 0px -10% 0px',
	threshold = 0.15,
	once = true
} = {}) {
	if (observer) observer.disconnect()
	tracked.clear()

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const el = entry.target
				const replay = el.getAttribute('data-wbk-replay') === 'true'

				if (entry.isIntersecting) {
					el.classList.add('wbk-in-view', 'wbk-played')
					if (once && !replay) {
						observer.unobserve(el)
						tracked.delete(el)
					}
				} else {
					if (replay) el.classList.remove('wbk-in-view')
				}
			})
		},
		{ root, rootMargin, threshold }
	)

	return observer
}

export function observeInViewElements({
	rootMargin = '0px 0px -10% 0px',
	threshold = 0.15,
	once = true
} = {}) {
	if (!observer) createInViewObserver({ rootMargin, threshold, once })

	const els = document.querySelectorAll(
		'[class*="wbk-anim-"], [class*="wbk-text-"]'
	)

	els.forEach((el) => {
		if (tracked.has(el)) return
		tracked.add(el)

		// Immediate first-pass: if already visible, mark now (prevents hidden-on-route)
		if (isInViewportNow(el, rootMargin, threshold)) {
			el.classList.add('wbk-in-view', 'wbk-played')
			if (once && el.getAttribute('data-wbk-replay') !== 'true') return
		}

		observer.observe(el)
	})
}

export function disconnectInViewObserver() {
	if (observer) observer.disconnect()
	tracked.clear()
	observer = null
}
